import { ChangeDetectorRef, Component } from '@angular/core';
import { OpsVendors } from '@interfaces/vendor-management-model';
import { paginationParams } from '@shared/config/table.config';
import { columnDefs, rtoSegments } from './vendors-list.config';
import { SearchService } from '@services/search.service';
import { ErrorMessages, Months } from '@constants/constants';
import { AuthService } from '@core/auth.service';
import { VendorService } from '@services/vendor.service';
import { Router, ActivatedRoute } from '@angular/router';
import {
  RtoTypeEnum,
  RoutesEnum,
  ButtonLabel,
  PageTitle,
  getNoDataMessage,
  EmeraldLabel,
  NavigationRoutes,
} from '@constants/global.enum';
import { truncateText } from '@utils/utils';
import { HiringRequestService } from '@app/pages/hiring-manager/hiring-request/hiring-request.service';
import { VendorResponse } from '@app/shared/interfaces/hiring-manager.model';
import { VendorDetails } from '@app/shared/interfaces/vendor.model';

@Component({
  selector: 'app-vendors-list',
  templateUrl: './vendors-list.component.html',
  styleUrl: './vendors-list.component.scss',
})
export class VendorsListComponent {
  displayData: OpsVendors[] = [];
  allVendors: OpsVendors[] = [];
  paginationParams = paginationParams;
  columnDefs = columnDefs;
  RtoTypeEnum = RtoTypeEnum;
  filterValue: string = '';
  totalCount: number = 0;
  imageSrc: string = 'assets/img/no-data-found.png';
  errorMsg: string = ErrorMessages.NoDataAvailable;
  isDataLoaded: boolean = true;
  months: string[] = Months;
  monthOptions: {
    value: string;
    label: string;
    month: string;
    year: string;
    selected?: boolean;
  }[] = [];
  selectedMonth: string = '';
  selectedYear: string = new Date().getFullYear().toString();
  leftPercentage: number = 20;
  middlePercentage: number = 60;
  rightPercentage: number = 20;
  employeeId: string | null = null;
  preSelectedValue: string = '';
  RoutesEnum = RoutesEnum;
  ButtonLabel = ButtonLabel;
  pageTitle = PageTitle.ListOfVendors;
  noDataMessage: string = getNoDataMessage('alt');
  EmeraldLabel = EmeraldLabel;
  defaultSegmentWidth = 20;
  vendorDetails: VendorDetails[] = [];
  NavigationRoutes = NavigationRoutes
  rtoSegments = rtoSegments
  showVendorProfile = false;
  selectedVendor: OpsVendors | null = null;

  // op-table style client-side sort + pagination (matches the vendor
  // persona open-positions table; emerald-datatable removed)
  sortField: string = 'vendorName';
  sortDir: 'asc' | 'desc' = 'asc';
  pageIndex = 1;
  pageSize = 10;

  constructor(
    private searchService: SearchService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private vendorService: VendorService,
    private vendor: HiringRequestService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit() {
    this.generateLast12Months();
     if(history.state.vendorData){
      this.navigationToVendorDetails(history.state.vendorData);
    }
    this.route.queryParams.subscribe(params => {
      const monthParam = params['month'];
      const yearParam = params['year'];
      if (monthParam && yearParam) {
        const formattedMonth = monthParam.toString().padStart(2, '0');
        const targetValue = `${formattedMonth}, ${yearParam}`;
        const matchingOption = this.monthOptions.find(option => option.value === targetValue);
        if (matchingOption) {
          this.monthOptions.forEach(option => option.selected = false);
          matchingOption.selected = true;
          this.preSelectedValue = matchingOption.value;
          this.selectedMonth = matchingOption.month;
          this.selectedYear = matchingOption.year;
        }
      } else {
        const preSelectedOption = this.monthOptions.find(
          (option) => option.selected,
        );
        if (preSelectedOption) {
          this.preSelectedValue = preSelectedOption.value;
          this.selectedMonth = preSelectedOption.month;
          this.selectedYear = preSelectedOption.year;
        }
      }
      this.loadVendorData();
    });
  }

  generateLast12Months() {
    const now = new Date();
    this.monthOptions = [];
    for (let i = 0; i < 12; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i);
      const monthLabel = monthDate.toLocaleString('default', {
        month: 'long',
        year: 'numeric',
      });
      const monthValue = `${(monthDate.getMonth() + 1).toString().padStart(2, '0')}, ${monthDate.getFullYear()}`;
      this.monthOptions.push({
        label: monthLabel,
        value: monthValue,
        month: (monthDate.getMonth() + 1).toString().padStart(2, '0'),
        year: monthDate.getFullYear().toString(),
        selected: i === 0,
      });
    }
  }

  searchVendors(event: KeyboardEvent) {
    this.filterValue = this.searchService.getFilteredValue(event);
    this.displayData = this.allVendors?.length
      ? this.searchService.getSearchedData(
          this.allVendors,
          this.filterValue?.toLowerCase(),
        )
      : [];
    this.totalCount = this.displayData?.length;
    this.pageIndex = 1;
  }

  loadVendorData(): void {
    this.isDataLoaded = false;
    this.vendorService
      .getOpsVendorList(this.selectedMonth, this.selectedYear)
      .subscribe({
        next: (response) => {
          this.allVendors = response?.data?.map((vendor) => ({
            ...vendor,
            regularPercentage: vendor?.regularPercentage !== undefined
              ? Number(Number(vendor?.regularPercentage).toFixed(2))
              : vendor?.regularPercentage,
            hybridPercentage: vendor?.hybridPercentage !== undefined
              ? Number(Number(vendor?.hybridPercentage).toFixed(2))
              : vendor?.hybridPercentage,
            partialHybridPercentage: vendor?.partialHybridPercentage !== undefined
              ? Number(Number(vendor?.partialHybridPercentage).toFixed(2))
              : vendor?.partialHybridPercentage,
            remotePercentage: vendor?.remotePercentage !== undefined
              ? Number(Number(vendor?.remotePercentage).toFixed(2))
              : vendor?.remotePercentage,
            }));

          this.allVendors = this.mergeVendorContacts(this.allVendors);

          this.displayData = [...this.allVendors];
          this.totalCount = this.displayData.length;
          this.pageIndex = 1;
          this.isDataLoaded = true;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.displayData = [];
          this.allVendors = [];
          this.totalCount = 0;
          this.isDataLoaded = true;
          this.errorMsg = 'Failed to load vendor data.';
          this.cdr.detectChanges();
        },
      });
  }

  clearInput() {
    this.filterValue = '';
    this.displayData = [...this.allVendors];
    this.totalCount = this.displayData?.length;
    this.pageIndex = 1;
  }

  /**
   * The API returns one row per vendor contact (POC), so the same vendor can
   * appear several times. Collapse to one row per vendor, comma-joining the
   * distinct contact names / emails / phones; other fields keep the first
   * non-empty value seen.
   */
  private mergeVendorContacts(vendors: OpsVendors[]): OpsVendors[] {
    const appendUnique = (current: string, addition: string): string => {
      const parts = (current || '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
      const value = (addition || '').trim();
      if (value && !parts.some((x) => x.toLowerCase() === value.toLowerCase())) {
        parts.push(value);
      }
      return parts.join(', ');
    };

    const byVendor = new Map<string, any>();
    for (const vendor of vendors || []) {
      const key =
        (vendor?.vendorName || '').trim().toLowerCase() || String(vendor?.vendorId ?? '');
      const existing = byVendor.get(key);
      if (!existing) {
        byVendor.set(key, {
          ...vendor,
          pocName: (vendor?.pocName || '').trim(),
          pocEmail: (vendor?.pocEmail || '').trim(),
          pocPhoneNo: (vendor?.pocPhoneNo || '').trim(),
        });
        continue;
      }
      existing.pocName = appendUnique(existing.pocName, vendor?.pocName);
      existing.pocEmail = appendUnique(existing.pocEmail, vendor?.pocEmail);
      existing.pocPhoneNo = appendUnique(existing.pocPhoneNo, vendor?.pocPhoneNo);
      for (const field of [
        'totalEmployeesHeadCount',
        'vendorContractExpiryDate',
        'vendorOnboardingDate',
        'regularPercentage',
        'hybridPercentage',
        'partialHybridPercentage',
        'remotePercentage',
      ]) {
        if (existing[field] == null || existing[field] === '') {
          existing[field] = (vendor as any)[field];
        }
      }
    }
    return Array.from(byVendor.values());
  }

  sortBy(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
    this.pageIndex = 1;
  }

  get sortedData(): any[] {
    const data = [...(this.displayData || [])];
    const f = this.sortField;
    if (!f) return data;
    data.sort((a: any, b: any) => {
      const av = a?.[f];
      const bv = b?.[f];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
      return this.sortDir === 'asc' ? cmp : -cmp;
    });
    return data;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil((this.displayData?.length || 0) / this.pageSize));
  }

  get pagedData(): any[] {
    const start = (this.pageIndex - 1) * this.pageSize;
    return this.sortedData.slice(start, start + this.pageSize);
  }

  get rangeStart(): number {
    return (this.displayData?.length || 0) === 0 ? 0 : (this.pageIndex - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.pageIndex * this.pageSize, this.displayData?.length || 0);
  }

  /** Page numbers to render; -1 is an ellipsis gap. */
  get pageWindow(): number[] {
    const total = this.totalPages;
    const cur = this.pageIndex;
    const out: number[] = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) out.push(i);
      return out;
    }
    out.push(1);
    const start = Math.max(2, cur - 1);
    const end = Math.min(total - 1, cur + 1);
    if (start > 2) out.push(-1);
    for (let i = start; i <= end; i++) out.push(i);
    if (end < total - 1) out.push(-1);
    out.push(total);
    return out;
  }

  goToPage(page: number): void {
    if (page === -1 || page < 1 || page > this.totalPages || page === this.pageIndex) return;
    this.pageIndex = page;
  }

  prevPage(): void {
    this.goToPage(this.pageIndex - 1);
  }

  nextPage(): void {
    this.goToPage(this.pageIndex + 1);
  }

  onPageSizeChange(event: Event): void {
    this.pageSize = +(event.target as HTMLSelectElement).value;
    this.pageIndex = 1;
  }

  trackByIndex(index: number): number {
    return index;
  }

  onMonthChange(event: any): void {
    let selectedValue: string | undefined;
    if (typeof event === 'string') {
      const monthOption = this.monthOptions.find(
        (option) => option.label === event,
      );
      selectedValue = monthOption ? monthOption.value : undefined;
    } else if (event.value) {
      selectedValue = event.value;
    }
    if (
      !selectedValue ||
      !this.monthOptions.some((option) => option.value === selectedValue)
    ) {
      console.error('Invalid month selected.');
      return;
    }

    const selectedOption = this.monthOptions.find(
      (option) => option.value === selectedValue,
    );
    if (selectedOption) {
      this.selectedMonth = selectedOption.month;
      this.selectedYear = selectedOption.year;
      this.loadVendorData();
    }
  }

  navigateToVendorDashboard(): void {
    this.router.navigate(['/ops-vendor-management']);
  }
  
  navigationToVendorDetails(data: OpsVendors): void {
    this.vendor.getVendorData().subscribe({
      next: (response: VendorResponse) => {
        const vendor = response.data?.vendors?.find(v => v.name === data.vendorName);
        this.selectedVendor = { ...data, vendorId: vendor?.id !== undefined ? Number(vendor.id) : 0 };
        this.showVendorProfile = true;
      },
    });

  }
}
